import {Location} from '@angular/common';
import {Injectable} from '@angular/core';
import {Router} from '@angular/router';
import {Observable} from 'rxjs';
import {AssertionUtils} from '../../common/utils/assertion-utils';
import {TranslateService} from '../../translation/translate.service';
import {ContentSwitcherDialogService} from '../content-switcher-prototype/content-switcher-dialog.service';
import {ContentSwitcherNavigationInput} from './content-switcher-navigation-input.interface';
import {RouteInfoEntry} from './route-info-entry.interface';
import {RouteNavigationInput} from './route-navigation-input.interface';

@Injectable({providedIn: 'root'})
export class NavigationHelperPrototypeService {
  public currentEntry: RouteInfoEntry;
  private navigationHistory: RouteInfoEntry[] = [];
  private readonly MAX_HISTORY_LENGTH = 100;

  public constructor(
    private readonly router: Router,
    private readonly location: Location,
    private readonly contentSwitcher: ContentSwitcherDialogService,
    private readonly translate: TranslateService
  ) {}

  public clearNavigationHistory(): void {
    this.navigationHistory = new Array(this.MAX_HISTORY_LENGTH);
    this.contentSwitcher.clearNavigationHistory();
  }

  public navigateWithRoute(routingInfo: RouteNavigationInput): void {
    if (this.currentEntry?.isDialog) {
      this.contentSwitcher.closeDialogButMaintainHistory();
    }

    this.setCurrentEntry({routeData: {route: routingInfo.route}, inputData: routingInfo.inputData, isDialog: false} as RouteInfoEntry, routingInfo.forgetCurrentEntry ?? false);
    // eslint-disable-next-line no-restricted-syntax
    this.router.navigateByUrl(routingInfo.route);
  }

  public navigateWithContentSwitcher<T = void>(contentSwitcherInfo: ContentSwitcherNavigationInput): Observable<T> {
    this.setCurrentEntry({inputData: contentSwitcherInfo.data, isDialog: true}, contentSwitcherInfo.forgetCurrentEntry ?? false);
    return this.contentSwitcher.navigateForward<T>(
      contentSwitcherInfo.componentInfo,
      contentSwitcherInfo.forgetCurrentEntry ?? false,
      contentSwitcherInfo.dialogWidth,
      contentSwitcherInfo.canShowBMSTheme ?? true
    );
  }

  private setCurrentEntry(entry: RouteInfoEntry, forgetCurrentEntry: boolean = false): void {
    if (!AssertionUtils.isNullOrUndefined(this.currentEntry) && !forgetCurrentEntry) {
      this.navigationHistory.push(this.currentEntry);
    }

    if (this.navigationHistory.length > this.MAX_HISTORY_LENGTH) {
      this.navigationHistory.shift();
    }

    this.currentEntry = entry;
  }

  public navigateBack<T>(data?: T): void {
    const previousEntry = this.currentEntry;
    this.currentEntry = this.navigationHistory.pop();

    if (previousEntry?.isDialog || this.currentEntry?.isDialog) {
      this.contentSwitcher.navigateBack<T>(data);
    } else if (!AssertionUtils.isNullOrUndefined(this.currentEntry) && this.currentEntry.isDialog === false) {
      this.router.navigateByUrl(this.currentEntry.routeData.route);
    } else {
      this.location.back();
    }
  }

  public getHeaderText(pageType: any, translationKey: string): string {
    let headerPrefix = '';

    if (pageType === 'Add' || pageType === 'Duplicate') {
      headerPrefix = this.translate.instant('ANGULAR_COMPONENT_LIBRARY.CRUD_OVERVIEW_DATA.NEW');
    } else if (pageType === 'Edit') {
      headerPrefix = this.translate.instant('ANGULAR_COMPONENT_LIBRARY.CRUD_OVERVIEW_DATA.EDIT');
    }

    if (headerPrefix === '') {
      return `${headerPrefix} ${this.translate.instant(translationKey, {count: 1})}`;
    } else if (pageType === 'Add' || pageType === 'Duplicate') {
      return `${headerPrefix} ${this.translate.instant(translationKey, {count: 1}).toLowerCase()}: `;
    } else {
      return `${headerPrefix} ${this.translate.instant(translationKey, {count: 1}).toLowerCase()} `;
    }
  }

  public resetNavigationHistory(): void {
    if (this.contentSwitcherDialogActive()) {
      this.contentSwitcher.resetContentSwitcher();
    }
    this.currentEntry = undefined;
    this.navigationHistory = [];
  }

  public resetContentSwitcher(): void {
    if (!this.currentEntry.isDialog) {
      throw new Error('Cannot reset content switcher outside of dialog');
    }

    const amountOfHistoryReset = this.contentSwitcher.resetContentSwitcher();

    this.navigationHistory = this.navigationHistory.slice(0, amountOfHistoryReset * -1);
    this.currentEntry = this.navigationHistory.pop();
  }

  public getCurrentEntryData<T = void>(): T {
    return this.currentEntry?.inputData as T;
  }

  public contentSwitcherDialogActive(): boolean {
    return this.currentEntry?.isDialog === true;
  }
}
